import './sepho-hills.css';
import { renderGameMessage, startBlockedMessage } from '../shared/game-message';

const SCRIPT_URL = '/build/games/sepho-hills/sepho-hills.min.js';

import imgGameIcon from '../../images/game-icon.svg';
import imgCar from './images/car.svg';
import imgCase from './images/case.webp';
import imgCity from './images/city.svg';
import imgCloud from './images/cloud.svg';
import imgRotate from './images/rotate-phone.svg';

export const WELCOME_CONFIG = {
    icon: imgGameIcon,
    title: 'Witaj w Beauty Voyage 🏎️✨',
    description: '<p>Przygotuj się na dynamiczną trasę pełną pagórków, wzniesień i spadków! ' +
        'Twoim zadaniem jest sterowanie cabrioletem i dojechanie do mety z walizką na pokładzie.</p>' +
        '<p>Uważaj jednak – zbyt gwałtowna jazda może sprawić, że walizka wypadnie z samochodu. Jeśli ją zgubisz, gra kończy się porażką, więc liczy się nie tylko szybkość, ale też kontrola!</p>',
    howToPlay: {
        heading: 'JAK GRAĆ?',
        body: '<ol>' +
            '<li>Steruj samochodem za pomocą lewej i prawej strony ekranu.</li>' +
            '<li>Jedź przez trasę, pokonuj wzniesienia i zjazdy, pilnując walizki.</li>' +
            '<li>Dotrzyj do mety jak najszybciej, aby zdobyć jak najlepszy wynik!</li>' +
            '</ol>',
    },
    scoring: {
        heading: 'PUNKTACJA',
        body: '<ul>' +
            '<li>✅ Punkty naliczają się za przebyty dystans, ale i czas.</li>' +
            '<li>🏁 Im dalej dojedziesz, tym lepszy wynik.</li>' +
            '<li>⏱️ Im szybciej dotrzesz do mety, tym więcej punktów zdobędziesz.</li>' +
            '</ul>' +
            '<p>Maksymalny wynik to 35 punktów.<br/>Gotowa? Ruszaj na trasę i sprawdź, czy uda Ci się dowieźć walizkę do mety! ✨</p>',
    },
};

// Hills presets — 1-5, from gentle to extreme.
// The server stores only the preset number; the arrays live here.
const HILLS_PRESETS = {
    1: [
        {amplitude: 40, wavelength: 1400, phase: 0.45},
        {amplitude: 20, wavelength: 700, phase: 1.85},
        {amplitude: 10, wavelength: 350, phase: 0.72},
        {amplitude: 5, wavelength: 140, phase: 2.30},
    ],
    2: [
        {amplitude: 72, wavelength: 1300, phase: 0.45},
        {amplitude: 40, wavelength: 680, phase: 1.85},
        {amplitude: 22, wavelength: 320, phase: 0.72},
        {amplitude: 9, wavelength: 130, phase: 2.30},
    ],
    3: [
        {amplitude: 110, wavelength: 1200, phase: 0.45},
        {amplitude: 60, wavelength: 600, phase: 1.85},
        {amplitude: 35, wavelength: 280, phase: 0.72},
        {amplitude: 15, wavelength: 120, phase: 2.30},
    ],
    4: [
        {amplitude: 55, wavelength: 900, phase: 0.10},
        {amplitude: 45, wavelength: 500, phase: 1.20},
        {amplitude: 30, wavelength: 250, phase: 2.50},
        {amplitude: 15, wavelength: 110, phase: 0.80},
    ],
    5: [
        {amplitude: 90, wavelength: 800, phase: 0.65},
        {amplitude: 65, wavelength: 400, phase: 2.10},
        {amplitude: 40, wavelength: 180, phase: 1.30},
        {amplitude: 20, wavelength: 90, phase: 0.40},
    ],
};

const DEFAULT_GAME_CONFIG = {
    trackLength: 8000,
    terrainSegmentWidth: 8,
    flatStartLength: 450,
    flatEndLength: 300,
    carStartX: 120,

    engineAccel: 850,
    reverseAccel: 450,
    gravity: 740,
    airGravityMultiplier: 0.60,
    drag: 0.18,
    airDrag: 0.30,
    maxSpeed: 950,
    maxReverseSpeed: 300,

    wheelbase: 54,
    carBodyWidth: 80,
    carBodyHeight: 14,
    wheelRadius: 12,

    cameraFollowRatio: 0.30,

    terrainBlendZone: 350,
    wallH: 22,
    wallW: 15,

    item: {
        radius: 12,
        radiusX: 20,
        radiusY: 30,
        restitution: 0.60,
        friction: 0.18,
        spinTransfer: 0.004,
        fallDuration: 1.0,
    },
};

const DEFAULT_ASSETS_CONFIG = {
    texts: {
        gameTitle: 'Beauty Voyage',
        gameSubtitle: 'Dojedź na metę jak najszybciej!',
        startButton: 'Start!',
        resultTitle: 'Meta!',
        resultTime: 'Twój czas',
        resultFail: 'Przedmiot wypadł!',
        resultProgress: 'Dotarłeś do',
        playAgain: 'Zagraj ponownie',
        loading: 'Ładowanie...',
        hudTime: 'Czas',
        hudSpeed: 'Prędkość',
        rotatePrompt: 'Obróć urządzenie poziomo, aby grać',
    },
    images: {
        car: imgCar,
        rotateIcon: imgRotate,
        city: imgCity,
        cloud: imgCloud,
        item: imgCase,
    },
    sounds: {
        engine: null,
        finish: null,
    },
    colors: {
        bgStart: '#80FFF6',
        bgEnd: '#FF66CC',
        hillsA: '#FF66CC',
        hillsB: '#FF4E31',
        hillsC: '#FFAF45',
        car: '#C62828',
        carCabin: '#B71C1C',
        carWindow: '#B3E5FC',
        item: '#FF8F00',
        wheelHubA: '#FF4E31',
        wheelHubB: '#FF66CC',
        wheelHubC: '#80FFF6',
    },
};

let gameInstance  = null;
let currentTaskId = null;
let sessionToken  = null;
let eventSeq      = 0;
let _startTime = null;
let _pausedMs = 0;
let _pauseStart = null;
let _orientationMql = null;
let _onLandscapeFullscreen = null;

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

function contentEl(modal) {
    return modal.querySelector('.game-modal__content');
}

function resetSessionState() {
    currentTaskId = null;
    sessionToken  = null;
    eventSeq      = 0;
    _startTime = null;
    _pausedMs = 0;
    _pauseStart = null;
}

function getElapsedMs() {
    if (_startTime === null) return 0;
    const activePause = _pauseStart !== null ? Date.now() - _pauseStart : 0;
    return Math.max(0, Date.now() - _startTime - _pausedMs - activePause);
}

function sendEvent(path, taskId, body) {
    if (!sessionToken) return;
    fetch(`/api/task/${taskId}/sepho-hills/${path}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({sessionToken, ...body}),
    }).catch(() => { /* fire-and-forget */
    });
}

function attachEventListeners(taskId, modal) {
    if (!gameInstance?.eventBus) return;

    gameInstance.eventBus.on('game:start', () => {
        sessionToken = gameInstance.sessionToken ?? null;
        eventSeq = 0;
        _startTime = Date.now();
        _pausedMs = 0;
        _pauseStart = null;
    });

    // First interaction: lock the game as started server-side (NEW -> STARTED,
    // records the daily check-in). A 409 means the play was already consumed
    // elsewhere — stop the running game and show the blocked message.
    gameInstance.eventBus.on('game:lock', () => {
        if (!sessionToken) return;
        fetch(`/api/task/${taskId}/sepho-hills/begin`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({sessionToken}),
        })
            .then(async (res) => {
                if (res.status !== 409) return;
                const data = await res.json().catch(() => ({}));
                if (gameInstance) {
                    try { gameInstance.destroy(); } catch { /* ignore */ }
                    gameInstance = null;
                }
                renderGameMessage(modal, startBlockedMessage(data?.error));
            })
            .catch(() => { /* fire-and-forget; submit locks defensively as a fallback */ });
    });

    gameInstance.eventBus.on('item:dropped', ({distancePct}) => {
        if (!sessionToken) return;
        eventSeq++;
        sendEvent('item-dropped', taskId, {seq: eventSeq, elapsedMs: getElapsedMs(), distancePct: distancePct ?? 0});
    });

    gameInstance.eventBus.on('item:caught', ({distancePct}) => {
        if (!sessionToken) return;
        eventSeq++;
        sendEvent('item-caught', taskId, {seq: eventSeq, elapsedMs: getElapsedMs(), distancePct: distancePct ?? 0});
    });

    gameInstance.eventBus.on('game:pause', () => {
        if (!sessionToken) return;
        _pauseStart = Date.now();
        eventSeq++;
        sendEvent('pause', taskId, {seq: eventSeq, elapsedMs: getElapsedMs()});
    });

    gameInstance.eventBus.on('game:resume', () => {
        if (!sessionToken) return;
        if (_pauseStart !== null) {
            _pausedMs += Date.now() - _pauseStart;
            _pauseStart = null;
        }
        eventSeq++;
        sendEvent('resume', taskId, {seq: eventSeq, elapsedMs: getElapsedMs()});
    });
}

// Mobile browser only — exclude desktop (fine-pointer) and installed PWAs,
// where element fullscreen is either unnecessary or unsupported.
function isMobileBrowser() {
    const isMobile = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || window.matchMedia('(display-mode: fullscreen)').matches
        || window.navigator.standalone === true;
    return isMobile && !isStandalone;
}

function requestGameFullscreen(el) {
    if (!el || document.fullscreenElement) return;
    const req = el.requestFullscreen ?? el.webkitRequestFullscreen;
    if (typeof req !== 'function') return;
    try {
        const p = req.call(el);
        // Browsers reject when not granted (no user activation, iOS, etc.) — ignore.
        if (p && typeof p.catch === 'function') p.catch(() => { /* not allowed */ });
    } catch { /* ignore */ }
}

function exitGameFullscreen() {
    if (!document.fullscreenElement) return;
    try {
        const p = (document.exitFullscreen ?? document.webkitExitFullscreen)?.call(document);
        if (p && typeof p.catch === 'function') p.catch(() => { /* ignore */ });
    } catch { /* ignore */ }
}

// After the player rotates to landscape, try to enter fullscreen so the game
// uses the whole viewport. Only on mobile browsers (not PWA). The rotation gives
// the browser the user-activation context most engines require for fullscreen.
// Rotating back to portrait exits fullscreen again.
function attachLandscapeFullscreen(container) {
    if (!isMobileBrowser()) return;
    _onLandscapeFullscreen = () => {
        if (window.matchMedia('(orientation: landscape)').matches) {
            requestGameFullscreen(container);
        } else {
            exitGameFullscreen();
        }
    };
    _orientationMql = window.matchMedia('(orientation: landscape)');
    _orientationMql.addEventListener?.('change', _onLandscapeFullscreen);
    window.addEventListener('orientationchange', _onLandscapeFullscreen);
    // Handle the case where the device is already in landscape on open.
    _onLandscapeFullscreen();
}

function detachLandscapeFullscreen() {
    if (_onLandscapeFullscreen) {
        _orientationMql?.removeEventListener?.('change', _onLandscapeFullscreen);
        window.removeEventListener('orientationchange', _onLandscapeFullscreen);
        _onLandscapeFullscreen = null;
        _orientationMql = null;
    }
    exitGameFullscreen();
}

export function renderSephoHills(modal, data, markCardDone) {
    contentEl(modal).innerHTML = '';
    resetSessionState();
    startGame(modal, data, markCardDone);
}

function startGame(modal, data, markCardDone) {
    const taskId = data.task.id;
    currentTaskId = taskId;
    const cfg = data.sephoHillsGame.config;

    const hills = HILLS_PRESETS[cfg.hillsPreset] ?? HILLS_PRESETS[2];
    const difficulty = cfg.difficultyMultiplier ?? 1.0;

    const el = contentEl(modal);
    const containerId = 'sh-game-container';
    const container = document.createElement('div');
    container.id = containerId;
    container.className = 'sh-game';
    el.appendChild(container);

    loadScript(SCRIPT_URL)
        .then(() => {
            if (typeof SephoHillsGame === 'undefined') {
                console.error('SephoHillsGame: script loaded but SephoHillsGame is not defined');
                return;
            }

            const assetsConfig = cfg.backgroundImageSrc
                ? {...DEFAULT_ASSETS_CONFIG, images: {...DEFAULT_ASSETS_CONFIG.images, city: cfg.backgroundImageSrc}}
                : DEFAULT_ASSETS_CONFIG;

            gameInstance = SephoHillsGame.init(containerId, {
                assetsConfig,
                gameConfig: {
                    ...DEFAULT_GAME_CONFIG,
                    hills,
                    difficulty,
                    trackLength: cfg.trackLength ?? DEFAULT_GAME_CONFIG.trackLength,
                    autoStart: true,
                },
                apiConfig: {
                    useMock: false,
                    endpoints: {
                        startGame: `/api/task/${taskId}/sepho-hills/start`,
                        submitResult: `/api/task/${taskId}/sepho-hills/submit`,
                        heartbeat: `/api/task/${taskId}/sepho-hills/heartbeat`,
                    },
                },
                callbacks: {
                    onError: (error) => {
                        if (error?.status === 409 && (error.data?.error === 'already_completed' || error.data?.error === 'game_in_progress')) {
                            renderGameMessage(modal, startBlockedMessage(error.data?.error));
                            return;
                        }
                        document.dispatchEvent(new CustomEvent('game-modal:request-close'));
                        alert(`Błąd gry: ${error}`);
                    },
                    onComplete: (result) => {
                        if (gameInstance) {
                            try {
                                gameInstance.destroy();
                            } catch { /* ignore */
                            }
                            gameInstance = null;
                        }
                        markCardDone({rankingPointsEarned: result.rankingPointsEarned ?? 0, redeemablePointsEarned: result.redeemablePointsEarned ?? 0, checkIn: result.checkIn});
                        document.dispatchEvent(new CustomEvent('game-modal:result-show', {
                            detail: {
                                rankingPoints: result.rankingPointsEarned ?? 0,
                                redeemablePoints: result.redeemablePointsEarned ?? 0,
                            },
                        }));
                    },
                },
            });

            attachEventListeners(taskId, modal);
            attachLandscapeFullscreen(container);
        })
        .catch((e) => console.error('SephoHillsGame: failed to load game script', e));
}

export async function forceEndSephoHills() {
    if (gameInstance) {
        try {
            if (currentTaskId !== null) {
                await fetch(`/api/task/${currentTaskId}/sepho-hills/flag-force-end`, { method: 'POST' });
            }
            gameInstance.resume();
            gameInstance.forceEnd();
        } catch { /* ignore */
        }
    }
}

export function pauseSephoHills() {
    if (gameInstance) {
        try {
            gameInstance.pause();
        } catch { /* ignore */ }
    }
}

export function resumeSephoHills() {
    if (gameInstance) {
        try {
            gameInstance.resume();
        } catch { /* ignore */ }
    }
}

export function teardownSephoHills(modal) {
    detachLandscapeFullscreen();
    if (gameInstance) {
        try {
            gameInstance.destroy();
        } catch { /* ignore */
        }
        gameInstance = null;
    }
    resetSessionState();
    const el = contentEl(modal);
    if (el) el.innerHTML = '';
}
